import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import reorderIcon from "../../../../public/images/ico_reorder.svg";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import ResourceCard from "./ResourceCard";
import {
  DropEvent,
  SectionGroup,
  cardDragId,
  dropCard,
  dropSection,
  sectionDragId,
} from "./resourceOrder";
import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";

interface ReorderListProps {
  groups: SectionGroup[];
  onChange: (groups: SectionGroup[]) => void;
}

type SortableHandle = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef"
>;

/**
 * The sensors of the reorder mode: the mouse, a touch on a phone, and the
 * keyboard (Space or Enter lifts, the arrow keys move, Space drops, Escape
 * cancels). A touch waits 150ms, so a swipe on a handle does not start a drag
 * by mistake.
 */
const useReorderSensors = () =>
  useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

interface DragHandleProps {
  label: string;
  // Absent on the lifted copy, which only shows the handle.
  sortable?: SortableHandle;
}

// The handle of a section header and of a card (Figma 83:13437).
const DragHandle: React.FC<DragHandleProps> = ({ label, sortable }) => (
  <div
    ref={sortable?.setActivatorNodeRef}
    className={styles.drag_handle}
    {...sortable?.attributes}
    {...sortable?.listeners}
    aria-label={label}
  >
    <img src={reorderIcon} alt="" width={24} height={24} />
  </div>
);

const useMoveLabel = () => {
  const { t } = useTranslation();

  return {
    card: (resource: ScheduleResourceResponse) =>
      t("Move {{name}}", { name: resource.title }),
    section: (group: SectionGroup) =>
      t("Move section {{name}}", { name: group.category.title }),
  };
};

// One card in the grid of its section. While the card is in the air, its
// place shows the drop marker.
const SortableCard: React.FC<{ resource: ScheduleResourceResponse }> = ({
  resource,
}) => {
  const moveLabel = useMoveLabel();
  const sortable = useSortable({ id: cardDragId(resource.id) });
  const { setNodeRef, transform, transition, isDragging } = sortable;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cls(styles.reorder_item, { [styles.card_slot]: isDragging })}
      data-testid="reorder-card"
    >
      <ResourceCard
        resource={resource}
        dragHandle={
          <DragHandle label={moveLabel.card(resource)} sortable={sortable} />
        }
      />
    </div>
  );
};

// The card that follows the pointer.
const LiftedCard: React.FC<{ resource: ScheduleResourceResponse }> = ({
  resource,
}) => {
  const moveLabel = useMoveLabel();

  return (
    <div className={styles.lifted_card}>
      <ResourceCard
        resource={resource}
        dragHandle={<DragHandle label={moveLabel.card(resource)} />}
      />
    </div>
  );
};

interface SectionCardsProps {
  group: SectionGroup;
  onDrop: (event: DropEvent) => void;
}

/**
 * The card grid of one section. Each section has a drag context of its own,
 * so a card can drop only inside its section (decision Q10).
 */
const SectionCards: React.FC<SectionCardsProps> = ({ group, onDrop }) => {
  const { t } = useTranslation();
  const sensors = useReorderSensors();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lifted = group.resources.find(
    (resource) => cardDragId(resource.id) === activeId,
  );

  if (group.resources.length === 0) {
    return (
      <div className={styles.empty_section}>
        {t("No resource in this section yet.")}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragEnd={(event) => {
        setActiveId(null);
        onDrop(event);
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={group.resources.map((resource) => cardDragId(resource.id))}
        strategy={rectSortingStrategy}
      >
        <div className={styles.card_grid}>
          {group.resources.map((resource) => (
            <SortableCard key={resource.id} resource={resource} />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {lifted ? <LiftedCard resource={lifted} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

interface SectionHeadProps {
  group: SectionGroup;
  sortable?: SortableHandle;
}

const SectionHead: React.FC<SectionHeadProps> = ({ group, sortable }) => {
  const moveLabel = useMoveLabel();

  return (
    <div className={styles.section_head}>
      <DragHandle label={moveLabel.section(group)} sortable={sortable} />
      <h3>{group.category.title}</h3>
    </div>
  );
};

interface SortableSectionProps {
  group: SectionGroup;
  onDropCard: (event: DropEvent) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  group,
  onDropCard,
}) => {
  const sortable = useSortable({ id: sectionDragId(group.category.id) });
  const { setNodeRef, transform, transition, isDragging } = sortable;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cls(styles.section_block, {
        [styles.section_slot]: isDragging,
      })}
      data-testid="reorder-section"
    >
      <SectionHead group={group} sortable={sortable} />
      <SectionCards group={group} onDrop={onDropCard} />
    </div>
  );
};

// The section that follows the pointer (Figma 83:25830). A plain copy: its
// cards do not sort.
const LiftedSection: React.FC<{ group: SectionGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const moveLabel = useMoveLabel();

  return (
    <div className={cls(styles.section_block, styles.lifted_section)}>
      <SectionHead group={group} />
      {group.resources.length === 0 ? (
        <div className={styles.empty_section}>
          {t("No resource in this section yet.")}
        </div>
      ) : (
        <div className={styles.card_grid}>
          {group.resources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              dragHandle={<DragHandle label={moveLabel.card(resource)} />}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The reorder mode of the Resources page (Figma 83:13437, 83:26633,
 * 83:25830): a drag handle on every section and on every card. The sections
 * sort as a list. The cards sort in the grid of their own section.
 */
const ReorderList: React.FC<ReorderListProps> = ({ groups, onChange }) => {
  const sensors = useReorderSensors();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lifted = groups.find(
    (group) => sectionDragId(group.category.id) === activeId,
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragEnd={(event) => {
        setActiveId(null);
        onChange(dropSection(groups, event));
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={groups.map((group) => sectionDragId(group.category.id))}
        strategy={verticalListSortingStrategy}
      >
        <div className={styles.section_list}>
          {groups.map((group) => (
            <SortableSection
              key={group.category.id}
              group={group}
              onDropCard={(event) =>
                onChange(dropCard(groups, group.category.id, event))
              }
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {lifted ? <LiftedSection group={lifted} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ReorderList;
